/**
 * CompareAnalyses - Multi-analysis comparison page.
 *
 * Auto-detects data format across mira04 JSON, advanced CSV, and standard CSV.
 * No mode toggle -- all analyses are shown and loaded with a unified loader.
 *
 * Supported formats:
 *   mira04 JSON    - growth/results.json [{obj_id, frames:[{frame_idx, area_pixels, growth_rate}]}]
 *                  - rugosity/results.json [{obj_id, frames:[{frame_idx, contrast,...}]}]
 *   Advanced CSV   - growth/*.csv in subfolder, ISO timestamps
 *                  - pigmentation/*.json    -> H, S, V with +-1o std per frame/object
 *                  - rle_data/*.json        -> frequency spectrum per frame/object
 *   Standard CSV   - measurements.csv at analysis root, ISO timestamps
 *                  - pigmentation_data.csv  -> H, S, V per frame/object
 *                  - rugosity_data.csv      -> frequency spectrum per frame/object
 *
 * All three tabs (Growth, Color, Texture) are available for all formats.
 *
 * Reachable at /compare?deviceBucket=<bucket>
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Checkbox, Paper, CircularProgress,
  Tabs, Tab, IconButton, Tooltip, Alert, Menu, MenuItem, Chip,
  TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Plot from '../utils/plotlyBundle';
import DownloadIcon from '@mui/icons-material/Download';
import { normalizeColonyLab } from '../utils/colonyLab';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getS3Client, getDynamoDBClient } from '../utils/awsClients';
import Navbar from '../components/Navbar';
import * as XLSX from 'xlsx';
import { Line, Scatter, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Filler, ChartTooltip, Legend, zoomPlugin
);

const HSVVisualization = React.lazy(() => import('../components/HSVVisualization'));

// AWS clients initialized lazily via awsClients.js (supports Identity Pool + static key fallback)
let s3 = null;
let dynamoDB = null;
async function ensureClients() {
  if (!s3) s3 = await getS3Client();
  if (!dynamoDB) dynamoDB = await getDynamoDBClient();
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ANALYSIS_COLORS = [
  '#2196F3', '#FF5722', '#4CAF50', '#9C27B0', '#FF9800', '#00BCD4',
  '#E91E63', '#8BC34A', '#673AB7', '#FFC107', '#009688', '#795548',
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Pre-sign an S3 key (1-hour expiry). */
const presign = (bucket, key) =>
  s3.getSignedUrl('getObject', { Bucket: bucket, Key: key, Expires: 3600 });

/**
 * Parse a CSV string into an array of row objects with lowercase header keys.
 * Handles quoted cells that contain commas.
 */
const parseCSV = (text) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  return lines.slice(1).reduce((acc, line) => {
    const cols = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { cols.push(current.trim()); current = ''; continue; }
      current += ch;
    }
    cols.push(current.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = cols[i] || ''; });
    acc.push(row);
    return acc;
  }, []);
};

/**
 * Apply monotonic (non-decreasing) enforcement to an array of area values.
 * Biological colonies only grow; apparent shrinkage is measurement noise.
 * Returns a new array where each value is >= all preceding values.
 */
const enforceMonotonic = (values) => {
  const result = [];
  let runningMax = -Infinity;
  for (const v of values) {
    runningMax = Math.max(runningMax, v);
    result.push(runningMax);
  }
  return result;
};

/**
 * Compute elapsed hours from the first timestamp in a series.
 * Accepts an array of Date objects; returns parallel array of numbers.
 */
const toElapsedHours = (dates) => {
  if (!dates.length) return [];
  const t0 = dates[0].getTime();
  return dates.map((d) => +((d.getTime() - t0) / 3600000).toFixed(4));
};

/**
 * Apply a centered moving average to an array of {x, y} points.
 * Used to smooth the growth rate signal.
 */
const movingAverage = (pts, windowSize = 5) => {
  if (pts.length === 0) return pts;
  const half = Math.floor(windowSize / 2);
  return pts.map((pt, i) => {
    const start = Math.max(0, i - half);
    const end = Math.min(pts.length, i + half + 1);
    const slice = pts.slice(start, end);
    const avgY = slice.reduce((sum, p) => sum + p.y, 0) / slice.length;
    return { x: pt.x, y: avgY };
  });
};

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */

/** Decode COCO RLE to a flat Uint8Array (column-major, same as Segmentationbeta). */
function decodeRLEForPreview(counts, size) {
  const [h, w] = size;
  const mask = new Uint8Array(h * w);
  let pos = 0;
  let val = 0;
  for (const run of counts) {
    for (let i = 0; i < run; i++) {
      if (pos < mask.length) mask[pos++] = val;
    }
    val = 1 - val;
  }
  // Transpose from column-major to row-major
  const rowMajor = new Uint8Array(h * w);
  for (let col = 0; col < w; col++) {
    for (let row = 0; row < h; row++) {
      rowMajor[row * w + col] = mask[col * h + row];
    }
  }
  return rowMajor;
}

function previewObjectColor(objId) {
  const COLORS = [
    [255, 99, 132], [54, 162, 235], [255, 206, 86], [75, 192, 192],
    [153, 102, 255], [255, 159, 64], [142, 68, 173], [26, 188, 156],
  ];
  return COLORS[objId % COLORS.length];
}

/** List image files under an S3 prefix (paginated, images only). */
async function listSourceImages(bucket, prefix) {
  await ensureClients();
  const p = prefix.endsWith('/') ? prefix : `${prefix}/`;
  const results = [];
  let token;
  do {
    const res = await s3.listObjectsV2({ Bucket: bucket, Prefix: p, ContinuationToken: token }).promise();
    (res.Contents || [])
      .filter(o => /\.(jpg|jpeg|png)$/i.test(o.Key))
      .forEach(o => results.push(o));
    token = res.IsTruncated ? res.NextContinuationToken : null;
  } while (token);
  return results.sort((a, b) => a.Key.localeCompare(b.Key));
}

/** Render masks onto an offscreen canvas at full source resolution over a loaded HTMLImageElement. */
function drawMasksOnImage(img, masks, srcW, srcH) {
  const canvas = document.createElement('canvas');
  canvas.width = srcW;
  canvas.height = srcH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, srcW, srcH);
  const imgData = ctx.getImageData(0, 0, srcW, srcH);
  const pixels = imgData.data;
  const alpha = 0.45;
  for (const [objId, maskInfo] of Object.entries(masks)) {
    if (!maskInfo?.counts || !maskInfo?.shape) continue;
    const [r, g, b] = previewObjectColor(parseInt(objId));
    const rowMajor = decodeRLEForPreview(maskInfo.counts, maskInfo.shape);
    for (let i = 0; i < srcH * srcW; i++) {
      if (rowMajor[i]) {
        const pi = i * 4;
        pixels[pi]     = Math.round(pixels[pi]     * (1 - alpha) + r * alpha);
        pixels[pi + 1] = Math.round(pixels[pi + 1] * (1 - alpha) + g * alpha);
        pixels[pi + 2] = Math.round(pixels[pi + 2] * (1 - alpha) + b * alpha);
      }
    }
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas;
}

/** Colony map thumbnail canvas with forwardRef so parent can call toDataURL(). */
const MaskCanvas = React.forwardRef(function MaskCanvas({ maskEntries }, ref) {
  const localRef = React.useRef(null);
  // Expose the canvas DOM element to the forwarded ref
  React.useImperativeHandle(ref, () => localRef.current);

  React.useEffect(() => {
    const canvas = localRef.current;
    if (!canvas || !maskEntries.length) return;
    const firstMask = maskEntries[0][1];
    if (!firstMask?.shape) return;
    const [srcH, srcW] = firstMask.shape;
    const maxDim = 180;
    const scale = Math.min(maxDim / srcW, maxDim / srcH, 1);
    const dw = Math.round(srcW * scale);
    const dh = Math.round(srcH * scale);
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, dw, dh);

    const scaleX = srcW / dw;
    const scaleY = srcH / dh;

    const imgData = ctx.getImageData(0, 0, dw, dh);
    const pixels = imgData.data;

    for (const [objId, maskInfo] of maskEntries) {
      if (!maskInfo?.counts || !maskInfo?.shape) continue;
      const [r, g, b] = previewObjectColor(parseInt(objId));
      const rowMajor = decodeRLEForPreview(maskInfo.counts, maskInfo.shape);
      const alpha = 160 / 255;
      for (let dy = 0; dy < dh; dy++) {
        const sy = Math.min(Math.floor(dy * scaleY), srcH - 1);
        for (let dx = 0; dx < dw; dx++) {
          const sx = Math.min(Math.floor(dx * scaleX), srcW - 1);
          if (rowMajor[sy * srcW + sx]) {
            const i = (dy * dw + dx) * 4;
            pixels[i]     = Math.min(255, pixels[i]     * (1 - alpha) + r * alpha);
            pixels[i + 1] = Math.min(255, pixels[i + 1] * (1 - alpha) + g * alpha);
            pixels[i + 2] = Math.min(255, pixels[i + 2] * (1 - alpha) + b * alpha);
            pixels[i + 3] = 255;
          }
        }
      }
    }
    ctx.putImageData(imgData, 0, 0);
  }, [maskEntries]);

  return <canvas ref={localRef} style={{ borderRadius: 4, border: '1px solid #333' }} />;
});

/** Renders a small mask-overlay canvas per analysis to show colony spatial layout. */
function ColonyMapPreview({ previewDataMap, selected, allAnalyses, objectMetaMap = {} }) {
  // Local lookup so map labels read the same names as the rest of the
  // compare view. Falls back to "Object N" for legacy analyses with no
  // sidecar.
  const nameFor = (analysisId, objId) => {
    const meta = objectMetaMap?.[analysisId];
    return (
      meta?.[objId]?.name ||
      meta?.[String(objId)]?.name ||
      `Object ${objId}`
    );
  };
  const canvasRefs = React.useRef({});
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuAnalysisId, setMenuAnalysisId] = React.useState(null);

  const analyses = selected
    .map(id => allAnalyses.find(a => a.analysis_id === id))
    .filter(Boolean);
  const hasPreviews = Object.keys(previewDataMap).some(id => selected.includes(id));
  if (!hasPreviews) return null;

  const handleMenuOpen = (e, analysisId) => {
    setMenuAnchor(e.currentTarget);
    setMenuAnalysisId(analysisId);
  };
  const handleMenuClose = () => { setMenuAnchor(null); setMenuAnalysisId(null); };

  const downloadMasked = (analysisId, name) => {
    const canvas = canvasRefs.current[analysisId];
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${name || analysisId}_colony_map.png`;
    link.click();
    handleMenuClose();
  };

  const downloadWithImage = async (analysisId, name) => {
    handleMenuClose();
    const analysis = allAnalyses.find(a => a.analysis_id === analysisId);
    const preview = previewDataMap[analysisId];
    if (!analysis || !preview) return;

    const bucket = analysis.s3_bucket || process.env.REACT_APP_USER_BUCKET;
    const sourcePath = analysis.source_path || '';
    if (!sourcePath) { alert('Source path not found for this analysis.'); return; }

    const images = await listSourceImages(bucket, sourcePath);
    if (!images.length) { alert('No source images found for download.'); return; }

    const lastFrame = preview.lastFrame ?? 0;
    const frameIdx = Math.min(lastFrame, images.length - 1);
    const imgUrl = presign(bucket, images[frameIdx].Key);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const masks = preview.masks || {};
      const firstMask = Object.values(masks)[0];
      const [srcH, srcW] = firstMask?.shape || [img.naturalHeight, img.naturalWidth];
      const canvas = drawMasksOnImage(img, masks, srcW, srcH);
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${name || analysisId}_colony_overlay.png`;
      link.click();
    };
    img.onerror = () => alert('Could not load source image.');
    img.src = imgUrl;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ color: '#aaa', mb: 1.5, fontWeight: 600 }}>
        Colony Map
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {analyses.map(a => {
          const preview = previewDataMap[a.analysis_id];
          if (!preview) return null;
          const maskEntries = Object.entries(preview.masks || {});
          return (
            <Box key={a.analysis_id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#888', maxWidth: 180, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.analysis_name || a.analysis_id}
              </Typography>
              <MaskCanvas maskEntries={maskEntries} ref={el => { canvasRefs.current[a.analysis_id] = el; }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200, justifyContent: 'center', mt: 0.5 }}>
                {maskEntries.map(([objId]) => {
                  const [r, g, b] = previewObjectColor(parseInt(objId));
                  return (
                    <Box key={objId} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `rgb(${r},${g},${b})` }} />
                      <Typography
                        variant="caption"
                        sx={{ color: '#ccc', fontSize: '0.65rem' }}
                        title={`${nameFor(a.analysis_id, objId)} (#${objId})`}
                      >
                        {nameFor(a.analysis_id, objId)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Tooltip title="Download colony map">
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, a.analysis_id)} sx={{ color: '#888', mt: 0.3 }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => {
          const a = allAnalyses.find(x => x.analysis_id === menuAnalysisId);
          downloadMasked(menuAnalysisId, a?.analysis_name);
        }}>
          Mask overlay (PNG)
        </MenuItem>
        <MenuItem onClick={() => {
          const a = allAnalyses.find(x => x.analysis_id === menuAnalysisId);
          downloadWithImage(menuAnalysisId, a?.analysis_name);
        }}>
          With source image (PNG)
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default function CompareAnalyses() {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const params = new URLSearchParams(location.search);
  const deviceBucket = params.get('deviceBucket') || '';

  /* -- State -- */
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [hasCompared, setHasCompared] = useState(false);
  const [tab, setTab] = useState(0);
  const [colorSubTab, setColorSubTab] = useState(0); // 0=Hue, 1=Saturation, 2=Value
  // Single-image compare colour space toggle: 'hsv' | 'lab' for the 3D scatter explorer.
  const [singleColorSpace, setSingleColorSpace] = useState('hsv');

  // Loaded data keyed by analysis_id
  const [growthDataMap, setGrowthDataMap] = useState({});
  const [pigmentDataMap, setPigmentDataMap] = useState({});
  const [rugosityDataMap, setRugosityDataMap] = useState({});
  const [morphologyDataMap, setMorphologyDataMap] = useState({});
  const [spiralDataMap, setSpiralDataMap] = useState({});
  const [previewDataMap, setPreviewDataMap] = useState({}); // { [aid]: { masks: {[objId]: {size, counts}}, lastFrame: number } }
  // Per-analysis phenotypic colony rows (only populated for single-image saves).
  // Each entry: { colonies: [{colony_id, area_pixels, mean_color_lab, circularity, ...}] }.
  const [phenoDataMap, setPhenoDataMap] = useState({});
  // Per-analysis object metadata sidecar (Analysis/{sid}/object_meta.json).
  // Each entry: { [objId]: { name, customColor, species_top1, ... } }.
  // Drives the chart labels so users see their colony names instead of
  // "Region N" / "Obj N". Missing/legacy analyses get an empty map and
  // every consumer falls back to the synthesized label.
  const [objectMetaMap, setObjectMetaMap] = useState({});
  const [hsvExplorerData, setHsvExplorerData] = useState(null);
  const [hsvExplorerOpen, setHsvExplorerOpen] = useState(false);
  const [hsvExplorerLabel, setHsvExplorerLabel] = useState('');

  const growthChartRef = useRef(null);
  const growthRateChartRef = useRef(null);
  const hueChartRef = useRef(null);
  const satChartRef = useRef(null);
  const valChartRef = useRef(null);
  const rugosityChartRef = useRef(null);

  // Resolve the user-given name for an object inside a specific analysis,
  // falling back to "Object N" when the analysis was saved before the
  // object_meta.json sidecar landed. Use this everywhere chart labels and
  // table rows reference an obj_id so renames flow through the entire
  // compare view consistently.
  const getObjectName = useCallback((analysisId, objId) => {
    const meta = objectMetaMap?.[analysisId];
    return (
      meta?.[objId]?.name ||
      meta?.[String(objId)]?.name ||
      `Object ${objId}`
    );
  }, [objectMetaMap]);

  /* -- Classification: single-image vs stack -- */
  const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp)$/i;
  const isSingleImageAnalysis = (a) => {
    if (!a) return false;
    const sp = a.source_path || '';
    if (IMAGE_EXT_RE.test(sp)) return true;
    const fc = Number(a.frame_count);
    return Number.isFinite(fc) && fc <= 1;
  };

  /* -- Derived: all analyses sorted oldest-first, split by kind -- */
  const filteredAnalyses = useMemo(
    () => [...allAnalyses].sort((a, b) => {
      const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tA - tB;
    }),
    [allAnalyses]
  );
  const singleImageAnalyses = useMemo(
    () => filteredAnalyses.filter(isSingleImageAnalysis),
    [filteredAnalyses]
  );
  const stackAnalyses = useMemo(
    () => filteredAnalyses.filter((a) => !isSingleImageAnalysis(a)),
    [filteredAnalyses]
  );

  /* -- Mode inferred from current selection. While nothing is selected the
        user can pick either column; the first pick locks the other column. */
  const selectedKind = useMemo(() => {
    if (!selected.length) return null;
    const set = new Set(singleImageAnalyses.map((a) => a.analysis_id));
    return selected.some((id) => set.has(id)) ? 'single' : 'stack';
  }, [selected, singleImageAnalyses]);
  const isSingleImageCompare = selectedKind === 'single';

  /* -- Available tabs (Texture hidden in single-image compares) -- */
  const availableTabs = useMemo(() => {
    const base = [
      { key: 'growth', label: isSingleImageCompare ? 'Area' : 'Growth' },
      { key: 'color', label: 'Color' },
    ];
    if (!isSingleImageCompare) base.push({ key: 'texture', label: 'Texture' });
    if (!isSingleImageCompare) base.push({ key: 'shape', label: 'Shape' });
    base.push({ key: 'spiral', label: 'Spiral' });
    return base;
  }, [isSingleImageCompare]);
  // Snap tab back to a valid one if Texture disappeared after the user
  // selected a single-image analysis.
  useEffect(() => {
    if (tab >= availableTabs.length) setTab(0);
  }, [availableTabs, tab]);

  /* -- Fetch all analyses from DynamoDB -- */
  useEffect(() => {
    if (!deviceBucket) return;
    (async () => {
      await ensureClients();
      try {
        const result = await dynamoDB.query({
          TableName: 'Analysis_folders',
          KeyConditionExpression: '#db = :db',
          FilterExpression: '(attribute_not_exists(#status) OR #status <> :deleted)',
          ExpressionAttributeNames: { '#db': 'device_bucket', '#status': 'status' },
          ExpressionAttributeValues: { ':db': deviceBucket, ':deleted': 'deleted' },
          ScanIndexForward: false,
        }).promise();
        setAllAnalyses(result.Items || []);
      } catch (err) {
        console.error(err);
        enqueueSnackbar('Unable to load analyses. Please try again.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceBucket, enqueueSnackbar]);

  /* -- Toggle selection (single-image and stack analyses can't be mixed) -- */
  const toggleSelectForKind = (id, kind) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // Switching column? Replace the selection rather than mixing kinds.
      if (selectedKind && selectedKind !== kind) return [id];
      return [...prev, id];
    });
  };
  const selectAllOf = (list) => setSelected(list.map((a) => a.analysis_id));
  const deselectAll = () => setSelected([]);

  /* -- List all S3 objects under a prefix (handles pagination) -- */
  const listAllObjects = async (bucket, prefix) => {
    await ensureClients();
    let contents = [];
    let token = null;
    do {
      const res = await s3.listObjectsV2({
        Bucket: bucket, Prefix: prefix,
        ...(token ? { ContinuationToken: token } : {}),
      }).promise();
      contents = contents.concat(res.Contents || []);
      token = res.IsTruncated ? res.NextContinuationToken : null;
    } while (token);
    return contents;
  };

  /* -- Load growth JSON for mira04 format -- */
  const loadMira04Growth = async (bucket, allContents, tsMap = {}) => {
    const key = allContents.find(o => o.Key.includes('/growth/') && o.Key.endsWith('results.json'))?.Key;
    if (!key) return null;
    try {
      const data = await fetch(presign(bucket, key)).then(r => r.json());
      if (!Array.isArray(data)) return null;
      // Normalise to the shared row format used by chart builders. Timestamps come
      // from the persisted frame_timestamps.json (tsMap, keyed by frame index);
      // when absent (older analyses) isoTime/date stay empty and the x-axis falls
      // back to frame index.
      return data.flatMap(obj =>
        (obj.frames || []).map(f => {
          const iso = tsMap[String(f.frame_idx)] || '';
          return {
            isoTime: iso,
            date: iso ? new Date(iso) : null,
            frame: f.frame_idx,
            object: String(obj.obj_id),
            area: f.area_pixels || 0,
            growthRate: f.growth_rate || 0,
          };
        })
      );
    } catch { return null; }
  };

  /* -- Load morphology JSON (shape + edge metrics over time) -- */
  const loadMira04Morphology = async (bucket, allContents, tsMap = {}) => {
    const key = allContents.find(o => o.Key.includes('/morphology/') && o.Key.endsWith('results.json'))?.Key;
    if (!key) return null;
    try {
      const data = await fetch(presign(bucket, key)).then(r => r.json());
      if (!Array.isArray(data)) return null;
      // Same envelope as growth: [{obj_id, frames:[{frame_idx, ...metrics}]}].
      // Ratio metrics are null on frames whose mask is empty — that means
      // "undefined", not zero, so they are passed through as null and the
      // chart leaves a gap rather than drawing a plunge to the axis.
      return data.flatMap(obj =>
        (obj.frames || []).map(f => {
          const iso = tsMap[String(f.frame_idx)] || '';
          return {
            isoTime: iso,
            date: iso ? new Date(iso) : null,
            frame: f.frame_idx,
            object: String(obj.obj_id),
            area: f.area_px ?? null,
            perimeter: f.perimeter_px ?? null,
            circularity: f.circularity ?? null,
            irregularity: f.irregularity ?? null,
            convexity: f.convexity ?? null,
            solidity: f.solidity ?? null,
            eccentricity: f.eccentricity ?? null,
            equivalentDiameter: f.equivalent_diameter_px ?? null,
            extent: f.extent ?? null,
            contourCount: f.contour_count ?? null,
            // Present only when the pass had the source frames available.
            textureContrast: f.texture_contrast ?? null,
            textureHomogeneity: f.texture_homogeneity ?? null,
            textureEnergy: f.texture_energy ?? null,
            textureCorrelation: f.texture_correlation ?? null,
            intensityMean: f.intensity_mean ?? null,
            intensityStd: f.intensity_std ?? null,
            edgeDensity: f.edge_density ?? null,
            textureMeasured: !!f.texture_measured,
          };
        })
      );
    } catch { return null; }
  };

  /* -- Load rugosity JSON for mira04 format -- */
  const loadMira04Rugosity = async (bucket, allContents) => {
    const key = allContents.find(o => o.Key.includes('/rugosity/') && o.Key.endsWith('results.json'))?.Key;
    if (!key) return null;
    try {
      const data = await fetch(presign(bucket, key)).then(r => r.json());
      if (!Array.isArray(data)) return null;
      // Normalise to frames structure: { frames: { frameIdx: { objId: {contrast,..., frequency_log, radial_profile_log} } } }
      const frames = {};
      data.forEach(obj => {
        (obj.frames || []).forEach(f => {
          const fi = String(f.frame_idx);
          if (!frames[fi]) frames[fi] = {};
          frames[fi][String(obj.obj_id)] = {
            contrast: f.contrast, homogeneity: f.homogeneity,
            energy: f.energy, correlation: f.correlation, edge_density: f.edge_density,
            frequency_log: f.frequency_log || null,
            radial_profile_log: f.radial_profile_log || null,
          };
        });
      });
      return { frames };
    } catch { return null; }
  };

  /* -- Load frame_idx -> ISO timestamp map from frame_timestamps.json (if present).
        Returns {} for older analyses that predate the sidecar. -- */
  const loadFrameTimestamps = async (bucket, allContents) => {
    const key = allContents.find(o => o.Key.endsWith('frame_timestamps.json'))?.Key;
    if (!key) return {};
    try {
      const data = await fetch(presign(bucket, key)).then(r => r.json());
      const iso = data?.frame_timestamps_iso;
      return (iso && typeof iso === 'object') ? iso : {};
    } catch { return {}; }
  };

  /* -- Load spiral plate analysis JSON -- */
  const loadSpiralAnalysis = async (bucket, allContents) => {
    const key = allContents.find(o => o.Key.includes('/spiral/') && o.Key.endsWith('results.json'))?.Key;
    if (!key) return null;
    try {
      const data = await fetch(presign(bucket, key)).then(r => r.json());
      if (!data || !data.frames) return null;
      return data;
    } catch { return null; }
  };

  /** Load the last frame's masks from rle_data/ per-frame JSON files for the preview.
   *  Per-frame format: {frame_idx: N, objects: [{obj_id, rle: {counts, size}}]} */
  const loadPreviewMasks = async (bucket, allContents) => {
    // Find all per-frame JSON files in rle_data/
    const rleFiles = allContents
      .filter(o => o.Key.includes('/rle_data/') && o.Key.endsWith('.json'))
      .sort((a, b) => b.Key.localeCompare(a.Key)); // descending -> last frame first
    if (!rleFiles.length) return null;
    // Load only the last frame file (largest frame index by filename sort)
    const lastFile = rleFiles[0];
    if (lastFile.Size && lastFile.Size > 20 * 1024 * 1024) return null;
    try {
      const data = await fetch(presign(bucket, lastFile.Key)).then(r => r.json());
      if (!data) return null;

      // Handle per-frame format: {frame_idx, objects: [{obj_id, rle: {counts, size}}]}
      if (data.objects && Array.isArray(data.objects)) {
        const masks = {};
        data.objects.forEach(obj => {
          if (obj.rle?.counts && obj.rle?.size) {
            masks[String(obj.obj_id)] = { counts: obj.rle.counts, shape: obj.rle.size };
          }
        });
        return Object.keys(masks).length ? { masks, lastFrame: data.frame_idx ?? 0 } : null;
      }
      // Legacy consolidated format: {frames: {frameKey: {objId: {counts, size}}}}
      if (data.frames) {
        const frameKeys = Object.keys(data.frames).map(Number).sort((a, b) => a - b);
        if (!frameKeys.length) return null;
        const lastFrameKey = String(frameKeys[frameKeys.length - 1]);
        return { masks: data.frames[lastFrameKey], lastFrame: frameKeys[frameKeys.length - 1] };
      }
      return null;
    } catch { return null; }
  };

  /* -- Load growth CSV for a STANDARD analysis -- */
  const loadStandardGrowth = async (bucket, prefix) => {
    const key = `${prefix}measurements.csv`;
    try {
      const url = presign(bucket, key);
      const text = await fetch(url).then((r) => r.text());
      const rows = parseCSV(text);
      if (!rows.length) return null;
      // Normalise columns: standard has 'time' (ISO), 'frame', 'object', 'area', 'image name'
      return rows.map((r) => {
        const isoTime = r.time || '';
        const date = new Date(isoTime);
        return {
          isoTime,
          date: isNaN(date.getTime()) ? null : date,
          frame: parseInt(r.frame, 10) || 0,
          object: r.object || '1',
          area: parseFloat(r.area) || 0,
          growthRate: 0,
          imageName: r['image name'] || '',
        };
      }).filter((r) => r.date !== null);
    } catch {
      return null;
    }
  };

  /* -- Load growth CSV for an ADVANCED analysis -- */
  const loadAdvancedGrowth = async (bucket, allContents) => {
    const growthKey = allContents.find(
      (o) => o.Key.includes('/growth/') && o.Key.endsWith('.csv')
    )?.Key;
    if (!growthKey) return null;
    try {
      const url = presign(bucket, growthKey);
      const text = await fetch(url).then((r) => r.text());
      const rows = parseCSV(text);
      if (!rows.length) return null;
      // Advanced CSV 'time' column contains ISO timestamps (same as standard).
      return rows.map((r) => {
        const isoTime = r.time || '';
        const date = new Date(isoTime);
        return {
          isoTime,
          date: isNaN(date.getTime()) ? null : date,
          frame: parseInt(r.frame, 10) || 0,
          object: r.object || '1',
          area: parseFloat(r.area) || 0,
          growthRate: 0,
        };
      }).filter((r) => r.date !== null);
    } catch {
      return null;
    }
  };

  /* -- Load pigmentation JSON (advanced only) -- */
  const loadPigmentation = async (bucket, allContents) => {
    const key = allContents.find(
      (o) => o.Key.includes('/pigmentation/') && o.Key.endsWith('.json')
    )?.Key;
    if (!key) return null;
    try {
      return await fetch(presign(bucket, key)).then((r) => r.json());
    } catch {
      return null;
    }
  };

  /* -- Load rugosity JSON (advanced rle_data only) -- */
  const loadRugosity = async (bucket, allContents) => {
    const key = allContents.find(
      (o) => o.Key.includes('/rle_data/') && o.Key.endsWith('.json')
    )?.Key;
    if (!key) return null;
    try {
      return await fetch(presign(bucket, key)).then((r) => r.json());
    } catch {
      return null;
    }
  };

  /**
   * Load pigmentation CSV for a STANDARD analysis.
   *
   * CSV format:
   *   Frame, Image Name, Object ID, Object Name, Hue (degrees), Saturation (0-255), Value (0-255)
   *
   * Normalises into the advanced-mode structure:
   *   { frames: { [frameIdx]: { [objId]: { hue, saturation, value, timestamp } } } }
   */
  const loadStandardPigmentation = async (bucket, prefix, growthRows) => {
    const candidates = [
      `${prefix}pigmentation_data.csv`,
    ];
    let text = null;
    for (const key of candidates) {
      try {
        text = await fetch(presign(bucket, key)).then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        });
        break;
      } catch { /* try next */ }
    }
    // Fallback: list prefix and find any pigmentation CSV
    if (!text) {
      try {
        const listing = await listAllObjects(bucket, prefix);
        const pigKey = listing.find(
          (o) => o.Key.includes('pigmentation') && o.Key.endsWith('.csv')
        )?.Key;
        if (pigKey) {
          text = await fetch(presign(bucket, pigKey)).then((r) => r.text());
        }
      } catch { /* no pigmentation data */ }
    }
    if (!text) return null;

    const rows = parseCSV(text);
    if (!rows.length) return null;

    // Build timestamp lookups from growth data.
    const imageNameToTimestamp = {};
    const frameToTimestamp = {};
    if (growthRows) {
      growthRows.forEach((r) => {
        if (r.isoTime) {
          frameToTimestamp[r.frame] = r.isoTime;
          const imgName = r.imageName || r.image_name || '';
          if (imgName) imageNameToTimestamp[imgName.toLowerCase()] = r.isoTime;
        }
      });
    }

    // Normalise into advanced-style structure
    const frames = {};
    rows.forEach((r) => {
      const rawFrame = parseInt(r.frame, 10) || 0;
      const frameIdx = String(rawFrame);
      const objId = r['object id'] || r.object || '1';
      if (!frames[frameIdx]) frames[frameIdx] = {};

      const imgName = (r['image name'] || '').toLowerCase();
      const ts =
        (imgName && imageNameToTimestamp[imgName]) ||
        frameToTimestamp[rawFrame] ||
        frameToTimestamp[rawFrame - 1] ||
        '';

      frames[frameIdx][objId] = {
        hue: parseFloat(r['hue (degrees)'] || r.hue || 0),
        saturation: parseFloat(r['saturation (0-255)'] || r.saturation || 0),
        value: parseFloat(r['value (0-255)'] || r.value || 0),
        timestamp: ts,
        frame_name: r['image name'] || '',
      };
    });
    return { frames };
  };

  /**
   * Load rugosity CSV for a STANDARD analysis.
   *
   * CSV format:
   *   Frame, Image Name, Object ID, Object Name, log10(Spatial Frequency), log10(Power)
   *
   * Normalises into the advanced-mode rugosity structure:
   *   { frames: { [frameIdx]: { [objId]: { frequency_log: [], radial_profile_log: [] } } } }
   */
  const loadStandardRugosity = async (bucket, prefix) => {
    let text = null;
    try {
      text = await fetch(presign(bucket, `${prefix}rugosity_data.csv`)).then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      });
    } catch { /* try listing */ }

    if (!text) {
      try {
        const listing = await listAllObjects(bucket, prefix);
        const rugKey = listing.find(
          (o) => o.Key.includes('rugosity') && o.Key.endsWith('.csv')
        )?.Key;
        if (rugKey) {
          text = await fetch(presign(bucket, rugKey)).then((r) => r.text());
        }
      } catch { /* no rugosity data */ }
    }
    if (!text) return null;

    const rows = parseCSV(text);
    if (!rows.length) return null;

    const frames = {};
    rows.forEach((r) => {
      const frameIdx = String(parseInt(r.frame, 10) || 0);
      const objId = r['object id'] || r.object || '1';
      if (!frames[frameIdx]) frames[frameIdx] = {};
      if (!frames[frameIdx][objId]) {
        frames[frameIdx][objId] = { frequency_log: [], radial_profile_log: [] };
      }
      const freq = parseFloat(r['log10(spatial frequency)'] || 0);
      const power = parseFloat(r['log10(power)'] || 0);
      frames[frameIdx][objId].frequency_log.push(freq);
      frames[frameIdx][objId].radial_profile_log.push(power);
    });
    return { frames };
  };

  /* -- Main compare handler -- */
  const handleCompare = useCallback(async () => {
    if (selected.length < 2) {
      enqueueSnackbar('Select at least two analyses to compare.', { variant: 'warning' });
      return;
    }
    setComparing(true);
    setHasCompared(false);
    const gMap = {};
    const moMap = {};
    const pMap = {};
    const rMap = {};
    const sMap = {};
    const phMap = {};
    const prevMap = {};
    const metaMap = {};

    for (const id of selected) {
      const item = allAnalyses.find((a) => a.analysis_id === id);
      if (!item) continue;
      const bucket = item.s3_bucket || process.env.REACT_APP_USER_BUCKET;
      let prefix = item.s3_location || `${deviceBucket}/Analysis/${item.session_id || item.analysis_id}/`;
      if (!prefix.endsWith('/')) prefix += '/';
      try {
        let allContents = await listAllObjects(bucket, prefix);
        // Fallback: CPU service saves to Analysis/{session_id}/ without device prefix.
        // Old DynamoDB records may store a device-prefixed path so the listing is empty.
        if (!allContents.length) {
          const analysisSegment = prefix.match(/Analysis\/.+/)?.[0];
          if (analysisSegment) allContents = await listAllObjects(bucket, analysisSegment);
        }
        // Frame -> ISO timestamp map: preferred x-axis source for mira04
        // analyses; empty for older ones, which then fall back to frame index.
        const tsMap = await loadFrameTimestamps(bucket, allContents);
        // Growth: try mira04 JSON -> advanced CSV -> standard CSV
        let rows = await loadMira04Growth(bucket, allContents, tsMap);
        if (!rows) rows = await loadAdvancedGrowth(bucket, allContents);
        if (!rows) rows = await loadStandardGrowth(bucket, prefix);
        if (rows && rows.length) gMap[id] = { rows };
        // Morphology: shape/edge metrics per frame (circularity, irregularity,
        // convexity...). Written by /cpu/precompute for every new analysis;
        // absent on analyses run before it existed, which simply yields no
        // morphology charts rather than an error.
        const morph = await loadMira04Morphology(bucket, allContents, tsMap);
        if (morph && morph.length) moMap[id] = { rows: morph };
        // Pigmentation: try advanced JSON first
        const pig = await loadPigmentation(bucket, allContents)
          || await loadStandardPigmentation(bucket, prefix, rows);
        if (pig) {
          // mira04 pigmentation JSON carries no per-frame timestamps; inject them
          // from tsMap so the x-axis and export show real time, not frame index.
          if (pig.frames && Object.keys(tsMap).length) {
            Object.entries(pig.frames).forEach(([fi, objs]) => {
              const iso = tsMap[String(fi)];
              if (iso && objs && typeof objs === 'object') {
                Object.values(objs).forEach((o) => {
                  if (o && o.timestamp == null) o.timestamp = iso;
                });
              }
            });
          }
          pMap[id] = pig;
        }
        // Rugosity: try mira04 JSON -> advanced JSON -> standard CSV
        const rug = await loadMira04Rugosity(bucket, allContents)
          || await loadRugosity(bucket, allContents)
          || await loadStandardRugosity(bucket, prefix);
        if (rug) rMap[id] = rug;
        // Spiral plate analysis
        const spiral = await loadSpiralAnalysis(bucket, allContents);
        if (spiral) sMap[id] = spiral;
        // Object metadata sidecar: user-given names + custom colors +
        // species labels saved alongside the analysis. NoSuchKey is the
        // expected case for analyses saved before this sidecar landed; we
        // silently swallow it and let consumers fall back to "Object N".
        try {
          const metaKey = allContents.find((o) =>
            o.Key.endsWith('object_meta.json')
          )?.Key;
          if (metaKey) {
            const metaText = await fetch(presign(bucket, metaKey)).then((r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.text();
            });
            const meta = JSON.parse(metaText);
            if (meta && typeof meta === 'object') metaMap[id] = meta;
          }
        } catch { /* legacy analysis without sidecar */ }
        // Phenotypic per-colony measurements (single-image colony-count saves).
        try {
          const phenoKey = allContents.find((o) =>
            o.Key.includes('/phenotypic/') && o.Key.endsWith('results.json')
          )?.Key;
          if (phenoKey) {
            const text = await fetch(presign(bucket, phenoKey)).then((r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.text();
            });
            const body = JSON.parse(text);
            if (Array.isArray(body?.colonies)) phMap[id] = body;
          }
        } catch { /* no phenotypic data for this analysis */ }
        // Preview masks: load last frame masks from rle_data
        const preview = await loadPreviewMasks(bucket, allContents);
        if (preview) prevMap[id] = preview;
      } catch (err) {
        console.error(`Failed to load data for ${id}:`, err);
      }
    }

    setGrowthDataMap(gMap);
    setPigmentDataMap(pMap);
    setPhenoDataMap(phMap);
    setRugosityDataMap(rMap);
    setMorphologyDataMap(moMap);
    setSpiralDataMap(sMap);
    setPreviewDataMap(prevMap);
    setObjectMetaMap(metaMap);
    setComparing(false);
    setHasCompared(true);

    const loaded = Object.keys(gMap).length;
    if (loaded === 0) {
      enqueueSnackbar('No data could be loaded for the selected analyses.', { variant: 'warning' });
    } else {
      enqueueSnackbar(`Loaded data for ${loaded} of ${selected.length} analyses.`, { variant: 'success' });
    }
  }, [selected, allAnalyses, deviceBucket, enqueueSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ================================================================ */
  /*  Chart Data Builders                                              */
  /* ================================================================ */

  /**
   * Growth chart - Area vs Time or Frame.
   *
   * For mira04 rows, date is null, so frame index is used as x-axis.
   * For timestamp-based rows, elapsed hours are used.
   */
  const growthChartData = useMemo(() => {
    const datasets = [];

    selected.forEach((id, aIdx) => {
      const entry = growthDataMap[id];
      if (!entry) return;
      const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      const color = ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length];

      const byObj = {};
      entry.rows.forEach((r) => {
        const key = r.object;
        if (!byObj[key]) byObj[key] = [];
        byObj[key].push(r);
      });

      Object.entries(byObj).forEach(([objId, objRows]) => {
        const hasDates = objRows.some(r => r.date !== null);
        if (hasDates) {
          objRows.sort((a, b) => a.date - b.date);
          const dates = objRows.map((r) => r.date);
          const elapsed = toElapsedHours(dates);
          const rawAreas = objRows.map((r) => r.area);
          const areas = enforceMonotonic(rawAreas);

          datasets.push({
            label: `${label} - ${getObjectName(id, objId)}`,
            data: elapsed.map((t, i) => ({ x: t, y: areas[i] })),
            borderColor: color,
            backgroundColor: color + '33',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.3,
            borderDash: aIdx > 0 ? [6, 3] : [],
          });
        } else {
          // Frame-based (mira04 format)
          objRows.sort((a, b) => a.frame - b.frame);
          const frames = objRows.map(r => r.frame);
          const rawAreas = objRows.map((r) => r.area);
          const areas = enforceMonotonic(rawAreas);

          datasets.push({
            label: `${label} - ${getObjectName(id, objId)}`,
            data: frames.map((f, i) => ({ x: f, y: areas[i] })),
            borderColor: color,
            backgroundColor: color + '33',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.3,
            borderDash: aIdx > 0 ? [6, 3] : [],
          });
        }
      });
    });

    return { datasets };
  }, [selected, growthDataMap, allAnalyses]);

  /* Morphology metrics selectable on one chart. Area is deliberately absent —
     the growth chart above already plots it, on the same x-axis. */
  const MORPH_METRICS = useMemo(() => ([
    { key: 'circularity',   label: 'Circularity',        hint: '1.0 = perfect circle' },
    { key: 'irregularity',  label: 'Irregularity',       hint: '1 − solidity; lobes and bays' },
    { key: 'convexity',     label: 'Edge convexity',     hint: 'hull perimeter ÷ actual; a crenulated rim lowers it' },
    { key: 'eccentricity',  label: 'Eccentricity',       hint: '0 = round, 1 = elongated' },
    { key: 'textureContrast',    label: 'Texture contrast',    hint: 'GLCM; surface roughness' },
    { key: 'textureHomogeneity', label: 'Texture homogeneity', hint: 'GLCM; surface uniformity' },
    { key: 'edgeDensity',        label: 'Edge density',        hint: 'detected edge fraction' },
    { key: 'intensityMean',      label: 'Mean intensity',      hint: 'grey level inside the region' },
  ]), []);
  const [morphMetric, setMorphMetric] = useState('circularity');

  const morphologyChartData = useMemo(() => {
    const datasets = [];
    selected.forEach((id, aIdx) => {
      const entry = morphologyDataMap[id];
      if (!entry) return;
      const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      const color = ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length];

      const byObj = {};
      entry.rows.forEach((r) => { (byObj[r.object] ||= []).push(r); });

      Object.entries(byObj).forEach(([objId, objRows]) => {
        const hasDates = objRows.some((r) => r.date !== null);
        if (hasDates) objRows.sort((a, b) => a.date - b.date);
        else objRows.sort((a, b) => a.frame - b.frame);
        const xs = hasDates ? toElapsedHours(objRows.map((r) => r.date))
                            : objRows.map((r) => r.frame);
        // null y-values are frames where the metric is UNDEFINED (empty mask).
        // spanGaps:false leaves a real gap instead of drawing through it —
        // joining across would invent a trend that was never measured.
        const pts = xs.map((x, i) => ({ x, y: objRows[i][morphMetric] ?? null }));
        if (!pts.some((p) => p.y != null)) return;   // metric absent for this analysis

        datasets.push({
          label: `${label} - ${getObjectName(id, objId)}`,
          data: pts,
          borderColor: color,
          backgroundColor: color + '33',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
          spanGaps: false,
          borderDash: aIdx > 0 ? [6, 3] : [],
        });
      });
    });
    return { datasets };
  }, [selected, morphologyDataMap, allAnalyses, morphMetric]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Determine x-axis label for growth chart */
  const growthXAxisLabel = useMemo(() => {
    for (const id of selected) {
      const entry = growthDataMap[id];
      if (entry?.rows?.some(r => r.date !== null)) return 'Elapsed Time (hours)';
    }
    return 'Frame';
  }, [selected, growthDataMap]);

  /**
   * Growth rate chart - derived from the growth data.
   */
  const growthRateChartData = useMemo(() => {
    const datasets = [];

    selected.forEach((id, aIdx) => {
      const entry = growthDataMap[id];
      if (!entry) return;
      const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      const color = ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length];

      const buildRate = (times, rawAreas) => {
        const areas = enforceMonotonic(rawAreas);
        const rate = [];
        for (let i = 1; i < areas.length; i++) {
          const dt = times[i] - times[i - 1];
          if (dt <= 0) continue;
          rate.push({
            x: times[i],
            y: (areas[i] - areas[i - 1]) / dt,
          });
        }
        return movingAverage(rate, 5);
      };

      const byObj = {};
      entry.rows.forEach((r) => {
        if (!byObj[r.object]) byObj[r.object] = [];
        byObj[r.object].push(r);
      });

      Object.entries(byObj).forEach(([objId, objRows]) => {
        const hasDates = objRows.some(r => r.date !== null);
        if (hasDates) {
          objRows.sort((a, b) => a.date - b.date);
          const times = toElapsedHours(objRows.map((r) => r.date));
          const areas = objRows.map((r) => r.area);
          const pts = buildRate(times, areas);
          if (!pts.length) return;

          datasets.push({
            label: `${label} - ${getObjectName(id, objId)}`,
            data: pts,
            borderColor: color,
            backgroundColor: color + '22',
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
            tension: 0.3,
            borderDash: aIdx > 0 ? [6, 3] : [],
          });
        } else {
          // Frame-based: always derive the growth rate from the monotonic
          // area curve. The backend's per-frame growth_rate is computed on
          // raw areas (mask noise -> negative rates) which the user does
          // not want surfaced. buildRate enforces monotonicity then takes
          // a moving average so the curve is always >= 0 and smooth.
          objRows.sort((a, b) => a.frame - b.frame);
          const frames = objRows.map((r) => r.frame);
          const areas = objRows.map((r) => r.area);
          const pts = buildRate(frames, areas);
          if (!pts.length) return;
          datasets.push({
            label: `${label} - ${getObjectName(id, objId)}`,
            data: pts,
            borderColor: color,
            backgroundColor: color + '22',
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
            tension: 0.3,
            borderDash: aIdx > 0 ? [6, 3] : [],
          });
        }
      });
    });

    return { datasets };
  }, [selected, growthDataMap, allAnalyses]);

  /**
   * Pigmentation comparison - H, S, V line charts over time per object.
   */
  const pigmentCompareData = useMemo(() => {
    const buildPropertyData = (property, stdProperty) => {
      const labelSortMap = {};
      const perAnalysis = [];

      selected.forEach((id, aIdx) => {
        const data = pigmentDataMap[id];
        if (!data?.frames) return;
        const analysisLabel =
          allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
        const color = ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length];

        const frameIdxs = Object.keys(data.frames)
          .map(Number)
          .sort((a, b) => a - b);

        const frames = [];
        frameIdxs.forEach((fIdx) => {
          const frameData = data.frames[String(fIdx)];
          if (!frameData) return;
          const firstObj = Object.values(frameData)[0];
          const ts = firstObj?.timestamp;
          let lbl;
          let sortKey = fIdx;
          if (ts) {
            const d = new Date(ts);
            if (!isNaN(d.getTime())) {
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              const hh = String(d.getHours()).padStart(2, '0');
              const min = String(d.getMinutes()).padStart(2, '0');
              lbl = `${mm}-${dd} ${hh}:${min}`;
              sortKey = d.getTime();
            }
          }
          if (!lbl) lbl = `Frame ${fIdx}`;
          frames.push({ label: lbl, frameData, sortKey });
        });

        frames.sort((a, b) => a.sortKey - b.sortKey);

        if (!frames.length) return;

        frames.forEach((f) => {
          if (!(f.label in labelSortMap)) labelSortMap[f.label] = f.sortKey;
        });

        perAnalysis.push({ id, aIdx, label: analysisLabel, color, frames });
      });

      if (!perAnalysis.length) return { labels: [], datasets: [] };

      const allLabels = Object.keys(labelSortMap)
        .sort((a, b) => labelSortMap[a] - labelSortMap[b]);

      const datasets = [];
      perAnalysis.forEach(({ id: aId, label: aLabel, color, frames, aIdx }) => {
        const labelToFrame = {};
        frames.forEach((f) => { labelToFrame[f.label] = f.frameData; });

        const objIds = new Set();
        frames.forEach((f) => {
          Object.keys(f.frameData).forEach((k) => objIds.add(k));
        });

        [...objIds].sort((a, b) => parseInt(a) - parseInt(b)).forEach((objId) => {
          const means = allLabels.map((lbl) => {
            const fd = labelToFrame[lbl];
            if (!fd || !fd[objId]) return null;
            const val = fd[objId][property];
            return val !== undefined ? val : null;
          });

          datasets.push({
            label: `${aLabel} - ${getObjectName(aId, objId)}`,
            data: means,
            borderColor: color,
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            tension: 0.3,
            spanGaps: true,
            borderDash: aIdx > 0 ? [6, 3] : [],
          });

          if (stdProperty) {
            const upper = allLabels.map((lbl) => {
              const fd = labelToFrame[lbl];
              if (!fd || !fd[objId]) return null;
              const m = fd[objId][property];
              const s = fd[objId][stdProperty];
              return m !== undefined && s !== undefined ? m + s : null;
            });
            const lower = allLabels.map((lbl) => {
              const fd = labelToFrame[lbl];
              if (!fd || !fd[objId]) return null;
              const m = fd[objId][property];
              const s = fd[objId][stdProperty];
              return m !== undefined && s !== undefined ? m - s : null;
            });

            datasets.push({
              label: `_hidden_upper_${aLabel}_${objId}`,
              data: upper,
              borderColor: 'transparent',
              backgroundColor: `${color}15`,
              borderWidth: 0,
              pointRadius: 0,
              fill: '+1',
              tension: 0.3,
              spanGaps: true,
            });
            datasets.push({
              label: `_hidden_lower_${aLabel}_${objId}`,
              data: lower,
              borderColor: 'transparent',
              backgroundColor: 'transparent',
              borderWidth: 0,
              pointRadius: 0,
              fill: false,
              tension: 0.3,
              spanGaps: true,
            });
          }
        });
      });

      return { labels: allLabels, datasets };
    };

    const hueData = buildPropertyData('hue', 'hue_std');
    const hasTimestamps = hueData.labels.some((lbl) => !lbl.startsWith('Frame '));
    const xAxisLabel = hasTimestamps ? 'Date & Time (MM-DD HH:MM)' : 'Frame';

    return {
      hue: hueData,
      saturation: buildPropertyData('saturation', 'saturation_std'),
      value: buildPropertyData('value', 'value_std'),
      xAxisLabel,
    };
  }, [selected, pigmentDataMap, allAnalyses]);

  /* -- Rugosity: frequency spectra from the last frame of each analysis -- */
  const rugosityChartData = useMemo(() => {
    const datasets = [];
    selected.forEach((id, aIdx) => {
      const data = rugosityDataMap[id];
      if (!data?.frames) return;
      const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      const color = ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length];

      const frameKeys = Object.keys(data.frames).map(Number).sort((a, b) => a - b);
      if (frameKeys.length === 0) return;
      const lastFrame = data.frames[String(frameKeys[frameKeys.length - 1])];
      if (!lastFrame) return;

      Object.entries(lastFrame).forEach(([objId, objData]) => {
        if (!objData?.frequency_log || !objData?.radial_profile_log) return;
        const pts = objData.frequency_log.map((f, i) => ({
          x: f, y: objData.radial_profile_log[i] || 0,
        }));

        datasets.push({
          label: `${label} - ${getObjectName(id, objId)}`,
          data: pts,
          borderColor: color,
          backgroundColor: color + '22',
          showLine: true,
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.3,
          borderDash: aIdx > 0 ? [6, 3] : [],
        });
      });
    });
    return { datasets };
  }, [selected, rugosityDataMap, allAnalyses]);

  /* -- Texture metrics radar data -- */
  const textureMetricsData = useMemo(() => {
    const METRICS = ['contrast', 'homogeneity', 'energy', 'correlation', 'edge_density'];
    const METRIC_LABELS = ['Contrast', 'Homogeneity', 'Energy', 'Correlation', 'Edge Density'];

    // Collect last-frame data for each analysis: { analysisLabel: { objId: {metric: value} } }
    const collected = {};
    selected.forEach((id) => {
      const data = rugosityDataMap[id];
      if (!data?.frames) return;
      const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      const frameKeys = Object.keys(data.frames).map(Number).sort((a, b) => a - b);
      if (!frameKeys.length) return;
      const lastFrame = data.frames[String(frameKeys[frameKeys.length - 1])];
      if (!lastFrame) return;
      collected[label] = {};
      Object.entries(lastFrame).forEach(([objId, objData]) => {
        const row = {};
        METRICS.forEach((m) => { row[m] = objData[m] ?? null; });
        if (Object.values(row).some((v) => v !== null)) {
          collected[label][objId] = row;
        }
      });
    });

    if (!Object.keys(collected).length) return null;

    // Collect all unique object IDs
    const allObjIds = [...new Set(Object.values(collected).flatMap((objMap) => Object.keys(objMap)))].sort();

    // Compute min/max per metric for normalization
    const metricRange = {};
    METRICS.forEach((m) => {
      const vals = Object.values(collected).flatMap((objMap) =>
        Object.values(objMap).map((row) => row[m]).filter((v) => v !== null)
      );
      metricRange[m] = {
        min: vals.length ? Math.min(...vals) : 0,
        max: vals.length ? Math.max(...vals) : 1,
      };
    });

    const normalize = (m, v) => {
      const { min, max } = metricRange[m];
      return max === min ? 0.5 : (v - min) / (max - min);
    };

    // Build one radar chart per object
    const charts = allObjIds.map((objId) => {
      const datasets = Object.entries(collected).map(([label, objMap], idx) => {
        const color = ANALYSIS_COLORS[idx % ANALYSIS_COLORS.length];
        const values = METRICS.map((m) => {
          const v = objMap[objId]?.[m];
          return v !== null && v !== undefined ? normalize(m, v) : null;
        });
        return {
          label,
          data: values,
          borderColor: color,
          backgroundColor: color + '22',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: true,
        };
      });
      return { objId, datasets, labels: METRIC_LABELS };
    });

    return { charts, collected, metricRange, METRICS, METRIC_LABELS };
  }, [selected, rugosityDataMap, allAnalyses]);

  /* -- Chart option builders -- */
  const morphOptions = useMemo(() => {
    const meta = MORPH_METRICS.find((m) => m.key === morphMetric);
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { color: '#ccc', usePointStyle: true, padding: 14, font: { size: 11 } } },
        zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(4) ?? '—'}`,
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          title: { display: true, text: growthXAxisLabel, color: '#aaa' },
          ticks: { color: '#888' },
          grid: { color: 'rgba(255,255,255,0.06)' },
        },
        y: {
          title: { display: true, text: meta ? meta.label : morphMetric, color: '#aaa' },
          ticks: { color: '#888' },
          grid: { color: 'rgba(255,255,255,0.06)' },
        },
      },
    };
  }, [morphMetric, growthXAxisLabel, MORPH_METRICS]);

  const growthOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { color: '#ccc', usePointStyle: true, padding: 14, font: { size: 11 } } },
      zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} px\u00b2`,
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: growthXAxisLabel, color: '#aaa' },
        ticks: { color: '#888' },
        grid: { color: 'rgba(255,255,255,0.06)' },
      },
      y: {
        title: { display: true, text: 'Area (px\u00b2)', color: '#aaa' },
        ticks: { color: '#888' },
        grid: { color: 'rgba(255,255,255,0.06)' },
        beginAtZero: true,
      },
    },
  }), [growthXAxisLabel]);

  const growthRateOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { color: '#ccc', usePointStyle: true, padding: 14, font: { size: 11 } } },
      zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: growthXAxisLabel, color: '#aaa' },
        ticks: { color: '#888' },
        grid: { color: 'rgba(255,255,255,0.06)' },
      },
      y: {
        title: { display: true, text: 'Growth Rate', color: '#aaa' },
        ticks: { color: '#888' },
        grid: { color: 'rgba(255,255,255,0.06)' },
        beginAtZero: true,
      },
    },
  }), [growthXAxisLabel]);

  const makeDistChartOptions = (title, yLabel, yMax, xLabel) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        ticks: { color: '#aaa', maxRotation: 45, autoSkip: true, maxTicksLimit: 20, font: { size: 10 } },
        grid: { color: 'rgba(255,255,255,0.06)' },
        title: { display: true, text: xLabel || 'Date & Time (MM-DD HH:MM)', color: '#aaa', font: { size: 12 } },
      },
      y: {
        min: 0,
        max: yMax,
        ticks: { color: '#aaa', font: { size: 11 } },
        grid: { color: 'rgba(255,255,255,0.06)' },
        title: { display: true, text: yLabel, color: '#aaa', font: { size: 13 } },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#ccc',
          usePointStyle: true,
          padding: 14,
          font: { size: 11 },
          filter: (item) => !item.text.startsWith('_hidden_'),
        },
      },
      title: { display: true, text: title, color: '#fff', font: { size: 15 } },
      tooltip: {
        filter: (item) => !item.dataset.label?.startsWith('_hidden_'),
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${typeof ctx.parsed.y === 'number' ? ctx.parsed.y.toFixed(2) : ctx.parsed.y}`,
        },
      },
      zoom: {
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' },
        pan: { enabled: true, mode: 'x' },
      },
    },
  });

  const pigXLabel = pigmentCompareData.xAxisLabel || 'Date & Time (MM-DD HH:MM)';
  const hueChartOptions = useMemo(() => makeDistChartOptions('Hue Over Time', 'Hue (degrees)', 360, pigXLabel), [pigXLabel]); // eslint-disable-line react-hooks/exhaustive-deps
  const satChartOptions = useMemo(() => makeDistChartOptions('Saturation Over Time', 'Saturation (0-255)', 260, pigXLabel), [pigXLabel]); // eslint-disable-line react-hooks/exhaustive-deps
  const valChartOptions = useMemo(() => makeDistChartOptions('Brightness (Value) Over Time', 'Value (0-255)', 260, pigXLabel), [pigXLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const lineOptions = (xLabel, yLabel) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { color: '#ccc', usePointStyle: true, padding: 14, font: { size: 11 } } },
      zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
    },
    scales: {
      x: { type: 'linear', title: { display: true, text: xLabel, color: '#aaa' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
      y: { title: { display: true, text: yLabel, color: '#aaa' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
    },
  });

  /* -- Utility actions -- */
  const downloadChart = (ref, name) => {
    const chart = ref.current;
    if (!chart) return;
    const a = document.createElement('a');
    a.href = chart.toBase64Image();
    a.download = `${name}.png`;
    a.click();
  };

  const resetZoom = (ref) => ref.current?.resetZoom();

  /**
   * Download all growth data (area + growth rate) as a multi-sheet XLSX.
   */
  const downloadGrowthXlsx = () => {
    if (!Object.keys(growthDataMap).length) return;

    const areaRows = [['Timestamp', 'Frame', 'Analysis', 'Region', 'Area_px2', 'Delta_px2']];
    const rateRows = [['Frame_or_Hour', 'Analysis', 'Region', 'Growth_Rate']];

    selected.forEach((id) => {
      const entry = growthDataMap[id];
      if (!entry) return;
      const analysisLabel = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;

      const byObj = {};
      entry.rows.forEach((r) => {
        if (!byObj[r.object]) byObj[r.object] = [];
        byObj[r.object].push(r);
      });

      Object.entries(byObj).forEach(([objId, objRows]) => {
        const hasDates = objRows.some(r => r.date !== null);
        if (hasDates) {
          objRows.sort((a, b) => a.date - b.date);
          const t0 = objRows[0].date.getTime();
          const monotonicAreas = enforceMonotonic(objRows.map((r) => r.area));

          objRows.forEach((r, i) => {
            const area = monotonicAreas[i];
            const delta = i === 0 ? '' : +(area - monotonicAreas[i - 1]).toFixed(2);
            areaRows.push([r.isoTime, r.frame, analysisLabel, objId, area, delta]);
          });

          const times = objRows.map((r) => +((r.date.getTime() - t0) / 3600000).toFixed(4));
          const smoothed = movingAverage(
            monotonicAreas.slice(1).map((a, i) => {
              const dt = times[i + 1] - times[i];
              return dt > 0 ? { x: times[i + 1], y: (a - monotonicAreas[i]) / dt } : null;
            }).filter(Boolean),
            5
          );
          smoothed.forEach((pt) => {
            rateRows.push([+pt.x.toFixed(4), analysisLabel, objId, +pt.y.toFixed(4)]);
          });
        } else {
          objRows.sort((a, b) => a.frame - b.frame);
          const monotonicAreas = enforceMonotonic(objRows.map((r) => r.area));
          objRows.forEach((r, i) => {
            const area = monotonicAreas[i];
            const delta = i === 0 ? '' : +(area - monotonicAreas[i - 1]).toFixed(2);
            areaRows.push(['', r.frame, analysisLabel, objId, area, delta]);
          });
          // CSV growth-rate column mirrors the chart: derive from monotonic
          // areas so the exported numbers match what is plotted (never
          // negative). The backend's r.growthRate field is intentionally
          // dropped here.
          const frames = objRows.map((r) => r.frame);
          const smoothed = movingAverage(
            monotonicAreas.slice(1).map((a, i) => {
              const df = frames[i + 1] - frames[i];
              return df > 0 ? { x: frames[i + 1], y: (a - monotonicAreas[i]) / df } : null;
            }).filter(Boolean),
            5,
          );
          smoothed.forEach((pt) => {
            rateRows.push([+pt.x.toFixed(4), analysisLabel, objId, +pt.y.toFixed(4)]);
          });
        }
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(areaRows), 'Area (px2)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rateRows), 'Growth Rate');
    XLSX.writeFile(wb, 'growth_comparison.xlsx');
  };

  /**
   * Download all pigmentation data as a multi-sheet XLSX.
   */
  const downloadPigmentXlsx = () => {
    if (!Object.keys(pigmentDataMap).length) return;
    const rows = [['Timestamp', 'Frame', 'Analysis', 'Object', 'Hue', 'Hue_Std', 'Saturation', 'Saturation_Std', 'Value', 'Value_Std']];
    selected.forEach((id) => {
      const pig = pigmentDataMap[id];
      if (!pig?.frames) return;
      const analysisLabel = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      Object.entries(pig.frames).forEach(([frameKey, objects]) => {
        Object.entries(objects).forEach(([objId, d]) => {
          rows.push([
            d.timestamp || '', Number(frameKey), analysisLabel, objId,
            d.hue ?? '', d.hue_std ?? '',
            d.saturation ?? '', d.saturation_std ?? '',
            d.value ?? '', d.value_std ?? '',
          ]);
        });
      });
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Pigmentation Data');
    XLSX.writeFile(wb, 'pigmentation_comparison.xlsx');
  };

  /**
   * Download all rugosity/texture data as a multi-sheet XLSX.
   */
  const downloadRugosityXlsx = () => {
    if (!Object.keys(rugosityDataMap).length) return;
    const metricRows = [['Frame', 'Analysis', 'Object', 'Contrast', 'Homogeneity', 'Energy', 'Correlation', 'Edge_Density']];
    const spectrumRows = [['Analysis', 'Object', 'Frame', 'log_Frequency', 'log_Power']];
    selected.forEach((id) => {
      const rug = rugosityDataMap[id];
      if (!rug) return;
      const analysisLabel = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
      Object.entries(rug).forEach(([objId, frames]) => {
        (Array.isArray(frames) ? frames : [frames]).forEach((f) => {
          metricRows.push([
            f.frame_idx ?? '', analysisLabel, objId,
            f.contrast ?? '', f.homogeneity ?? '', f.energy ?? '', f.correlation ?? '', f.edge_density ?? '',
          ]);
          if (f.frequency_log && f.radial_profile_log) {
            f.frequency_log.forEach((freq, i) => {
              spectrumRows.push([analysisLabel, objId, f.frame_idx ?? '', freq, f.radial_profile_log[i] ?? '']);
            });
          }
        });
      });
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(metricRows), 'Texture Metrics');
    if (spectrumRows.length > 1) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(spectrumRows), 'Power Spectrum');
    }
    XLSX.writeFile(wb, 'texture_comparison.xlsx');
  };

  /* -- Chart toolbar (reusable) -- */
  const ChartToolbar = ({ chartRef, downloadName }) => (
    <Box>
      <Tooltip title="Reset zoom">
        <IconButton size="small" onClick={() => resetZoom(chartRef)}>
          <RestartAltIcon sx={{ color: '#aaa' }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download as PNG">
        <IconButton size="small" onClick={() => downloadChart(chartRef, downloadName)}>
          <DownloadIcon sx={{ color: '#aaa' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  /* -- Detect whether results have any data -- */
  const hasGrowth = Object.keys(growthDataMap).length > 0;
  const hasPigment = Object.keys(pigmentDataMap).length > 0;
  const hasRugosity = Object.keys(rugosityDataMap).length > 0;
  const hasMorphology = Object.keys(morphologyDataMap).length > 0;
  const hasSpiral = Object.keys(spiralDataMap).length > 0;
  const hasAnyData = hasGrowth || hasPigment || hasRugosity || hasSpiral;

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */

  if (!deviceBucket) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h6" color="error">
          No device specified. Navigate to your device and select "Compare" from the analysis view.
        </Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#121212', color: '#eee' }}>
      <Navbar />
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CompareArrowsIcon sx={{ fontSize: 28, color: '#7c3bed' }} />
          <Typography variant="h5" fontWeight={600}>Compare Analyses</Typography>
        </Box>

        {/* Analysis selector — two columns: single-image vs stack. Cross-mixing
            is intentionally disabled (selecting in the other column replaces the
            current selection) because the comparison metrics differ. */}
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
            {[
              { kind: 'single', list: singleImageAnalyses, title: 'Single-image analyses', subtitle: 'colony-count on one frame' },
              { kind: 'stack', list: stackAnalyses, title: 'Image-stack analyses', subtitle: 'time-lapse / video' },
            ].map(({ kind, list, title, subtitle }, colIdx) => {
              const dimmed = selectedKind && selectedKind !== kind;
              const selectedInCol = list.filter((a) => selected.includes(a.analysis_id)).length;
              return (
                <Box
                  key={kind}
                  sx={{
                    borderLeft: { md: colIdx === 1 ? '1px solid #2a2a4a' : 'none' },
                    opacity: dimmed ? 0.5 : 1,
                    transition: 'opacity 0.15s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #2a2a4a' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#ddd', fontWeight: 600 }}>{title}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {subtitle}{!loading ? ` · ${selectedInCol}/${list.length} selected` : ''}
                      </Typography>
                    </Box>
                    {!loading && list.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          onClick={() => selectAllOf(list)}
                          disabled={dimmed || selectedInCol === list.length}
                          sx={{ color: '#bba6f7', fontSize: '0.7rem', textTransform: 'none', minWidth: 0, py: 0.2, px: 0.8 }}
                        >
                          Select all
                        </Button>
                        <Button
                          size="small"
                          onClick={deselectAll}
                          disabled={selectedInCol === 0}
                          sx={{ color: '#aaa', fontSize: '0.7rem', textTransform: 'none', minWidth: 0, py: 0.2, px: 0.8 }}
                        >
                          Clear
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{
                    maxHeight: 280, overflow: 'auto', px: 1, py: 0.5,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#333', borderRadius: 3 },
                  }}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={22} sx={{ color: '#7c3bed' }} />
                      </Box>
                    ) : list.length === 0 ? (
                      <Typography sx={{ color: '#777', py: 2, px: 1, fontSize: '0.85rem' }}>
                        No {kind === 'single' ? 'single-image' : 'image-stack'} analyses for this device.
                      </Typography>
                    ) : (
                      list.map((a) => {
                        const isSelected = selected.includes(a.analysis_id);
                        return (
                          <Box
                            key={a.analysis_id}
                            onClick={() => toggleSelectForKind(a.analysis_id, kind)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 0.75,
                              borderRadius: 1, cursor: 'pointer',
                              bgcolor: isSelected ? 'rgba(124, 59, 237, 0.12)' : 'transparent',
                              border: '1px solid',
                              borderColor: isSelected ? 'rgba(124, 59, 237, 0.35)' : 'transparent',
                              mb: 0.5,
                              transition: 'all 0.15s',
                              '&:hover': { bgcolor: isSelected ? 'rgba(124, 59, 237, 0.18)' : 'rgba(255,255,255,0.04)' },
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              disableRipple
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelectForKind(a.analysis_id, kind)}
                              sx={{ p: 0, color: '#777', '&.Mui-checked': { color: '#7c3bed' } }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#e0e0e0', fontWeight: isSelected ? 500 : 400, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {a.analysis_name || a.analysis_id}
                              </Typography>
                              {a.created_at && (
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                  {new Date(a.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              )}
                            </Box>
                            {isSelected && (
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#7c3bed', flexShrink: 0 }} />
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {selectedKind && (
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #2a2a4a', bgcolor: 'rgba(124,59,237,0.06)' }}>
              <Typography variant="caption" sx={{ color: '#bba6f7' }}>
                Comparing {selectedKind === 'single' ? 'single-image' : 'image-stack'} analyses.
                Single-image and image-stack analyses can't be mixed.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Compare button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            disabled={selected.length < 2 || comparing}
            startIcon={comparing ? <CircularProgress size={16} color="inherit" /> : <CompareArrowsIcon />}
            onClick={handleCompare}
            sx={{ bgcolor: '#7c3bed', '&:hover': { bgcolor: '#6a2bc7' }, '&.Mui-disabled': { bgcolor: '#3a3a5a', color: '#666' }, textTransform: 'none', px: 3 }}
          >
            {comparing ? 'Loading...' : `Compare${selected.length >= 2 ? ` (${selected.length})` : ''}`}
          </Button>
          {selected.length === 1 && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              Select at least one more analysis to compare.
            </Typography>
          )}
        </Box>

        {/* Results */}
        {hasCompared && hasAnyData && (
          <>
            <ColonyMapPreview
              previewDataMap={previewDataMap}
              selected={selected}
              allAnalyses={allAnalyses}
              objectMetaMap={objectMetaMap}
            />
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ mb: 2, '& .MuiTab-root': { color: '#aaa', textTransform: 'none' }, '& .Mui-selected': { color: '#7c3bed' } }}
              TabIndicatorProps={{ sx: { bgcolor: '#7c3bed' } }}
            >
              {availableTabs.map((t, i) => (
                <Tab key={t.key} label={t.label} value={i} />
              ))}
            </Tabs>

            {/* Growth / Area Tab */}
            {availableTabs[tab]?.key === 'growth' && isSingleImageCompare && (
              <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#eee' }}>Area distribution</Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      Each dot is one colony. X = rank within analysis, Y = colony area in pixels.
                    </Typography>
                  </Box>
                </Box>
                {(() => {
                  const datasets = selected.map((id, i) => {
                    const pheno = phenoDataMap[id];
                    if (!pheno?.colonies?.length) return null;
                    const sorted = [...pheno.colonies].sort(
                      (a, b) => (a.area_pixels ?? 0) - (b.area_pixels ?? 0),
                    );
                    const color = ANALYSIS_COLORS[i % ANALYSIS_COLORS.length];
                    const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
                    return {
                      label,
                      data: sorted.map((c, idx) => ({ x: idx + 1, y: c.area_pixels ?? 0 })),
                      backgroundColor: color,
                      borderColor: color,
                      pointRadius: 3,
                      pointHoverRadius: 6,
                    };
                  }).filter(Boolean);
                  if (!datasets.length) {
                    return (
                      <Typography sx={{ color: '#888', py: 4, textAlign: 'center' }}>
                        No phenotypic data found in the selected analyses.
                      </Typography>
                    );
                  }
                  const traces = datasets.map((d) => ({
                    type: 'scatter', mode: 'markers',
                    name: d.label,
                    x: d.data.map((p) => p.x),
                    y: d.data.map((p) => p.y),
                    marker: { size: 7, color: d.backgroundColor, line: { color: '#222', width: 0.5 } },
                    hovertemplate: `${d.label}<br>rank %{x}<br>%{y} px<extra></extra>`,
                  }));
                  return (
                    <Box sx={{ height: 460 }}>
                      <Plot
                        data={traces}
                        layout={{
                          paper_bgcolor: '#1e1e2e', plot_bgcolor: '#1e1e2e',
                          font: { color: '#ccc' },
                          legend: { font: { color: '#ddd' } },
                          margin: { t: 10, r: 10, b: 50, l: 60 },
                          dragmode: 'pan',
                          xaxis: { title: 'Colony rank (small → large)', gridcolor: '#333', zerolinecolor: '#333', color: '#bbb' },
                          yaxis: { title: 'Area (px)', gridcolor: '#333', zerolinecolor: '#333', color: '#bbb' },
                        }}
                        config={{ responsive: true, scrollZoom: true, displaylogo: false, modeBarButtonsToRemove: ['select2d', 'lasso2d', 'toggleSpikelines'] }}
                        style={{ width: '100%', height: '100%' }}
                        useResizeHandler
                      />
                    </Box>
                  );
                })()}
              </Paper>
            )}
            {availableTabs[tab]?.key === 'growth' && !isSingleImageCompare && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Area Over Time</Typography>
                      {hasGrowth && (
                        <Tooltip title="Download raw data as XLSX">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadGrowthXlsx}
                            sx={{ fontSize: '0.72rem', color: '#aaa', borderColor: '#444', textTransform: 'none', py: 0.3 }}
                          >
                            XLSX
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                    <ChartToolbar chartRef={growthChartRef} downloadName="growth_area_comparison" />
                  </Box>
                  {growthChartData.datasets.length > 0 ? (
                    <Box sx={{ height: 400 }}>
                      <Line ref={growthChartRef} data={growthChartData} options={growthOptions} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No growth data available for the selected analyses.
                    </Typography>
                  )}
                </Paper>

                <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Growth Rate</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        Area change per unit time, corrected for measurement noise.
                      </Typography>
                    </Box>
                    <ChartToolbar chartRef={growthRateChartRef} downloadName="growth_rate_comparison" />
                  </Box>
                  {growthRateChartData.datasets.length > 0 ? (
                    <Box sx={{ height: 350 }}>
                      <Line ref={growthRateChartRef} data={growthRateChartData} options={growthRateOptions} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      Insufficient data to compute growth rate.
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {/* Color Tab */}
            {availableTabs[tab]?.key === 'color' && isSingleImageCompare && (
              <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#eee' }}>
                      {singleColorSpace === 'hsv' ? 'HSV explorer' : 'CIELAB explorer'} (3D)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      Each dot is one colony, coloured by analysis.
                      {singleColorSpace === 'hsv'
                        ? ' Axes: Hue, Saturation, Value.'
                        : ' Axes: L*, a*, b*.'}
                      &nbsp;Drag to orbit · scroll to zoom · double-click to reset.
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    size="small" exclusive value={singleColorSpace}
                    onChange={(_, v) => v && setSingleColorSpace(v)}
                    sx={{
                      '& .MuiToggleButton-root': {
                        color: '#bbb', borderColor: '#444',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        color: '#fff', bgcolor: 'rgba(124,77,255,0.25)',
                        '&:hover': { bgcolor: 'rgba(124,77,255,0.35)' },
                      },
                    }}
                  >
                    <ToggleButton value="hsv">HSV</ToggleButton>
                    <ToggleButton value="lab">CIELAB</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {(() => {
                  // Lab → RGB → HSV per colony for each selected analysis.
                  const lab2rgb = (L, a, b) => {
                    let y = (L + 16) / 116, x = a / 500 + y, z = y - b / 200;
                    const f = (t) => (t * t * t > 0.008856 ? t * t * t : (t - 16 / 116) / 7.787);
                    x = 95.047 * f(x); y = 100.0 * f(y); z = 108.883 * f(z);
                    x /= 100; y /= 100; z /= 100;
                    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
                    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
                    let bl = x * 0.0557 + y * -0.2040 + z * 1.0570;
                    const gam = (u) => (u > 0.0031308 ? 1.055 * Math.pow(u, 1 / 2.4) - 0.055 : 12.92 * u);
                    return [gam(r), gam(g), gam(bl)].map((u) => Math.max(0, Math.min(1, u)));
                  };
                  const rgb2hsv = ([r, g, b]) => {
                    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
                    let h = 0;
                    if (d) {
                      if (mx === r) h = ((g - b) / d) % 6;
                      else if (mx === g) h = (b - r) / d + 2;
                      else h = (r - g) / d + 4;
                      h *= 60; if (h < 0) h += 360;
                    }
                    return [h, mx ? d / mx : 0, mx];
                  };
                  const traces = selected.map((id, i) => {
                    const pheno = phenoDataMap[id];
                    if (!pheno?.colonies?.length) return null;
                    const color = ANALYSIS_COLORS[i % ANALYSIS_COLORS.length];
                    const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
                    const pts = [];
                    for (const c of pheno.colonies) {
                      const lab = normalizeColonyLab(c.mean_color_lab);
                      if (!lab) continue;
                      if (singleColorSpace === 'hsv') {
                        const [h, s, v] = rgb2hsv(lab2rgb(lab[0], lab[1], lab[2]));
                        pts.push({ x: h, y: s * 100, z: v * 100 });
                      } else {
                        pts.push({ x: lab[0], y: lab[1], z: lab[2] });
                      }
                    }
                    if (!pts.length) return null;
                    return {
                      type: 'scatter3d', mode: 'markers',
                      name: label,
                      x: pts.map((p) => p.x),
                      y: pts.map((p) => p.y),
                      z: pts.map((p) => p.z),
                      marker: { size: 6, color, opacity: 0.95, line: { width: 0 } },
                      hovertemplate: singleColorSpace === 'hsv'
                        ? `${label}<br>H %{x:.0f}°<br>S %{y:.0f}%<br>V %{z:.0f}%<extra></extra>`
                        : `${label}<br>L %{x:.1f}<br>a* %{y:.1f}<br>b* %{z:.1f}<extra></extra>`,
                    };
                  }).filter(Boolean);
                  if (!traces.length) {
                    return (
                      <Typography sx={{ color: '#888', py: 4, textAlign: 'center' }}>
                        No colour data in the selected analyses.
                      </Typography>
                    );
                  }
                  // Plotly 3D needs the object form for axis title to render
                  // reliably; LAB gets autoscale because real values cluster
                  // inside the theoretical [-128, 128] bounds.
                  const axis = (text, range) => ({
                    title: { text, font: { color: '#bbb', size: 12 } },
                    ...(range ? { range } : { autorange: true }),
                    gridcolor: '#333', color: '#bbb',
                    backgroundcolor: '#1e1e2e', showbackground: true,
                    tickfont: { color: '#bbb', size: 10 },
                  });
                  const layout = {
                    paper_bgcolor: '#1e1e2e', plot_bgcolor: '#1e1e2e',
                    font: { color: '#ccc' },
                    legend: { font: { color: '#ddd' } },
                    margin: { t: 0, r: 0, b: 0, l: 0 },
                    scene: {
                      bgcolor: '#1e1e2e',
                      aspectmode: 'cube',
                      ...(singleColorSpace === 'hsv' ? {
                        xaxis: axis('Hue (°)', [0, 360]),
                        yaxis: axis('Saturation (%)', [0, 100]),
                        zaxis: axis('Value (%)', [0, 100]),
                      } : {
                        xaxis: axis('L* (lightness)'),
                        yaxis: axis('a* (green ⇄ red)'),
                        zaxis: axis('b* (blue ⇄ yellow)'),
                      }),
                      camera: { eye: { x: 1.5, y: 1.5, z: 1.2 } },
                    },
                  };
                  return (
                    <Box sx={{ height: 520 }}>
                      <Plot
                        data={traces}
                        layout={layout}
                        config={{ responsive: true, scrollZoom: true, displaylogo: false, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }}
                        style={{ width: '100%', height: '100%' }}
                        useResizeHandler
                      />
                    </Box>
                  );
                })()}
              </Paper>
            )}
            {availableTabs[tab]?.key === 'color' && !isSingleImageCompare && (
              <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" fontWeight={600}>Color Distribution Over Time</Typography>
                    {selected.some(id => pigmentDataMap[id]) && (
                      <Tooltip title="Download raw data as XLSX">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={downloadPigmentXlsx}
                          sx={{ fontSize: '0.72rem', color: '#aaa', borderColor: '#444', textTransform: 'none', py: 0.3 }}
                        >
                          XLSX
                        </Button>
                      </Tooltip>
                    )}
                    {selected.some(id => pigmentDataMap[id]) && (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          backgroundColor: '#00695c', fontSize: '0.75rem',
                          '&:hover': { backgroundColor: '#004d40' }, color: '#fff'
                        }}
                        onClick={() => {
                          const merged = { frames: {} };
                          const mergedMeta = {};
                          selected.forEach((id) => {
                            const pig = pigmentDataMap[id];
                            if (!pig?.frames) return;
                            const label = allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id;
                            const fkeys = Object.keys(pig.frames);
                            fkeys.forEach(fk => {
                              if (!merged.frames[fk]) merged.frames[fk] = {};
                              Object.entries(pig.frames[fk]).forEach(([objId, val]) => {
                                const mergedId = `${label}__${objId}`;
                                merged.frames[fk][mergedId] = val;
                                mergedMeta[mergedId] = { name: `${label} - ${getObjectName(id, objId)}` };
                              });
                            });
                          });
                          setHsvExplorerData(merged);
                          setHsvExplorerLabel(mergedMeta);
                          setHsvExplorerOpen(true);
                        }}
                      >
                        Explore
                      </Button>
                    )}
                  </Box>
                  <ChartToolbar
                    chartRef={colorSubTab === 0 ? hueChartRef : colorSubTab === 1 ? satChartRef : valChartRef}
                    downloadName={`color_${['hue', 'saturation', 'value'][colorSubTab]}_comparison`}
                  />
                </Box>

                <Tabs
                  value={colorSubTab}
                  onChange={(_, v) => setColorSubTab(v)}
                  sx={{
                    mb: 2,
                    minHeight: 32,
                    '& .MuiTab-root': { color: '#aaa', textTransform: 'none', minHeight: 32, py: 0.5, fontSize: '0.85rem' },
                    '& .Mui-selected': { color: '#7c3bed' },
                  }}
                  TabIndicatorProps={{ sx: { bgcolor: '#7c3bed' } }}
                >
                  <Tab label="Hue" value={0} />
                  <Tab label="Saturation" value={1} />
                  <Tab label="Value (Brightness)" value={2} />
                </Tabs>

                {colorSubTab === 0 && (
                  pigmentCompareData.hue.datasets.length > 0 ? (
                    <Box sx={{ height: 450 }}>
                      <Line ref={hueChartRef} data={pigmentCompareData.hue} options={hueChartOptions} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No hue data available for the selected analyses.
                    </Typography>
                  )
                )}

                {colorSubTab === 1 && (
                  pigmentCompareData.saturation.datasets.length > 0 ? (
                    <Box sx={{ height: 450 }}>
                      <Line ref={satChartRef} data={pigmentCompareData.saturation} options={satChartOptions} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No saturation data available for the selected analyses.
                    </Typography>
                  )
                )}

                {colorSubTab === 2 && (
                  pigmentCompareData.value.datasets.length > 0 ? (
                    <Box sx={{ height: 450 }}>
                      <Line ref={valChartRef} data={pigmentCompareData.value} options={valChartOptions} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No brightness data available for the selected analyses.
                    </Typography>
                  )
                )}
              </Paper>
            )}

            {/* Texture Tab */}
            {availableTabs[tab]?.key === 'texture' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Power Spectrum Chart */}
                <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Power Spectral Density</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          Log-log FFT radial profile. Higher power at low frequencies = smoother surface.
                        </Typography>
                      </Box>
                      {hasRugosity && (
                        <Tooltip title="Download raw data as XLSX">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadRugosityXlsx}
                            sx={{ fontSize: '0.72rem', color: '#aaa', borderColor: '#444', textTransform: 'none', py: 0.3, ml: 1 }}
                          >
                            XLSX
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                    <ChartToolbar chartRef={rugosityChartRef} downloadName="power_spectrum_comparison" />
                  </Box>
                  {rugosityChartData.datasets.length > 0 ? (
                    <Box sx={{ height: 450 }}>
                      <Scatter ref={rugosityChartRef} data={rugosityChartData} options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom', labels: { color: '#ccc', usePointStyle: true, padding: 14, font: { size: 11 } } },
                          zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.dataset.label}: freq=${ctx.parsed.x.toFixed(2)}, power=${ctx.parsed.y.toFixed(2)}`,
                            },
                          },
                        },
                        scales: {
                          x: { type: 'linear', title: { display: true, text: 'log(Spatial Frequency)', color: '#aaa', font: { size: 12 } }, grid: { color: '#333' }, ticks: { color: '#aaa' } },
                          y: { type: 'linear', title: { display: true, text: 'log(Power)', color: '#aaa', font: { size: 12 } }, grid: { color: '#333' }, ticks: { color: '#aaa' } },
                        },
                      }} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No power spectrum data available. Ensure analyses were computed with texture analysis enabled.
                    </Typography>
                  )}
                </Paper>

                {/* GLCM Metrics Table */}
                {textureMetricsData && textureMetricsData.charts && (
                  <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>GLCM Texture Metrics (Last Frame)</Typography>
                    {textureMetricsData.charts.map((chart) => (
                      <Box key={chart.objId} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>Colony {chart.objId}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {textureMetricsData.METRICS.map((m, mi) => {
                            const vals = chart.datasets.map((ds) => ds.data[mi]).filter((v) => v !== null);
                            if (!vals.length) return null;
                            return (
                              <Box key={m} sx={{ bgcolor: '#2a2a3a', borderRadius: 1, p: 1.5, minWidth: 130 }}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{textureMetricsData.METRIC_LABELS[mi]}</Typography>
                                {chart.datasets.map((ds, di) => {
                                  const raw = Object.values(textureMetricsData.collected)[di]?.[chart.objId]?.[m];
                                  return raw != null ? (
                                    <Box key={ds.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ds.borderColor, flexShrink: 0 }} />
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {raw.toFixed(4)}
                                      </Typography>
                                    </Box>
                                  ) : null;
                                })}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            )}

            {/* Spiral Tab */}
            {availableTabs[tab]?.key === 'shape' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Shape &amp; edge over time</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {MORPH_METRICS.find((m) => m.key === morphMetric)?.hint}
                      </Typography>
                    </Box>
                    <TextField
                      select
                      size="small"
                      value={morphMetric}
                      onChange={(e) => setMorphMetric(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 200, '& .MuiInputBase-input': { color: '#ddd', fontSize: '0.8rem' } }}
                    >
                      {MORPH_METRICS.map((m) => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                      ))}
                    </TextField>
                  </Box>
                  {hasMorphology ? (
                    <Box sx={{ height: 380 }}>
                      <Line data={morphologyChartData} options={morphOptions} />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#888', py: 4, textAlign: 'center' }}>
                      No shape metrics for the selected analyses. They are written by the
                      analysis pipeline from this release on; analyses run earlier need a
                      re-run (Segmentation &rarr; re-precompute) to gain them.
                    </Typography>
                  )}
                  {hasMorphology && morphologyChartData.datasets.length === 0 && (
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      This metric was not measured for the selected analyses &mdash; texture
                      metrics require the source frames.
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {availableTabs[tab]?.key === 'spiral' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {hasSpiral ? (() => {
                  const SECTOR_COLORS = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40'];
                  const analysisIds = selected.filter((id) => spiralDataMap[id]);
                  const analysisLabels = analysisIds.map((id) =>
                    allAnalyses.find((a) => a.analysis_id === id)?.analysis_name || id
                  );

                  // Build per-analysis summary
                  const summaries = analysisIds.map((id) => {
                    const data = spiralDataMap[id];
                    const frameKeys = Object.keys(data.frames || {});
                    const cfg = data.plate_config || {};
                    let totalColonies = 0;
                    const aggBySector = {};
                    frameKeys.forEach((fk) => {
                      const fd = data.frames[fk];
                      totalColonies += fd.summary?.total_colonies || 0;
                      (fd.sectors || []).forEach((s) => {
                        if (!aggBySector[s.sector]) aggBySector[s.sector] = { sector: s.sector, totalCount: 0, totalVolume: 0 };
                        aggBySector[s.sector].totalCount += s.colony_count;
                        aggBySector[s.sector].totalVolume += s.volume_uL || 0;
                      });
                    });
                    return {
                      id,
                      totalColonies,
                      sectors: Object.values(aggBySector).sort((a, b) => a.sector - b.sector),
                      numFrames: frameKeys.length,
                      detection: data.detection_method || 'unknown',
                      numSectors: cfg.num_sectors || 6,
                      numQuadrants: cfg.num_quadrants || 4,
                    };
                  });

                  // Comparison bar chart: total colonies per analysis
                  const totalBarData = {
                    labels: analysisLabels,
                    datasets: [{
                      label: 'Total Colonies',
                      data: summaries.map((s) => s.totalColonies),
                      backgroundColor: analysisIds.map((_, i) => ANALYSIS_COLORS[i % ANALYSIS_COLORS.length] + '99'),
                      borderColor: analysisIds.map((_, i) => ANALYSIS_COLORS[i % ANALYSIS_COLORS.length]),
                      borderWidth: 1,
                    }],
                  };
                  const totalBarOpts = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc' },
                    },
                    scales: {
                      x: { ticks: { color: '#888' }, grid: { color: '#222' } },
                      y: { title: { display: true, text: 'Colony Count', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' }, beginAtZero: true },
                    },
                  };

                  // Per-sector grouped bar chart
                  const maxSectors = Math.max(...summaries.map((s) => s.numSectors), 6);
                  const sectorLabels = Array.from({ length: maxSectors }, (_, i) => `Sector ${i + 1}`);
                  const sectorBarData = {
                    labels: sectorLabels,
                    datasets: analysisIds.map((id, aIdx) => {
                      const s = summaries[aIdx];
                      return {
                        label: analysisLabels[aIdx],
                        data: sectorLabels.map((_, si) => {
                          const sec = s.sectors.find((x) => x.sector === si + 1);
                          return sec ? sec.totalCount : 0;
                        }),
                        backgroundColor: ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length] + '77',
                        borderColor: ANALYSIS_COLORS[aIdx % ANALYSIS_COLORS.length],
                        borderWidth: 1,
                      };
                    }),
                  };
                  const sectorBarOpts = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top', labels: { color: '#ccc' } },
                      tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc' },
                    },
                    scales: {
                      x: { ticks: { color: '#888' }, grid: { color: '#222' } },
                      y: { title: { display: true, text: 'Colony Count', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' }, beginAtZero: true },
                    },
                  };

                  return (
                    <>
                      {/* Summary cards */}
                      <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Colony Count Comparison</Typography>
                        <Box sx={{ height: 300 }}>
                          <Bar data={totalBarData} options={totalBarOpts} />
                        </Box>
                      </Paper>

                      <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Colony Distribution by Sector</Typography>
                        <Box sx={{ height: 350 }}>
                          <Bar data={sectorBarData} options={sectorBarOpts} />
                        </Box>
                      </Paper>

                      {/* Summary table */}
                      <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Analysis Summary</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#ccc' }}>Analysis</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#ccc' }}>Total Colonies</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#ccc' }}>Frames</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#ccc' }}>Grid</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#ccc' }}>Detection</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {summaries.map((s, i) => (
                                <TableRow key={s.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ANALYSIS_COLORS[i % ANALYSIS_COLORS.length] }} />
                                      <Typography variant="body2">{analysisLabels[i]}</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{s.totalColonies}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.numFrames}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.numQuadrants}Q x {s.numSectors}S</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip label={s.detection} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>

                      {/* Per-sector CFU/mL comparison */}
                      <Paper sx={{ bgcolor: '#1e1e2e', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>CFU/mL by Sector (Aggregated)</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#ccc' }}>Sector</TableCell>
                                {analysisLabels.map((label, i) => (
                                  <TableCell key={i} align="right" sx={{ fontWeight: 700, color: '#ccc' }}>{label}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sectorLabels.map((label, si) => (
                                <TableRow key={si} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SECTOR_COLORS[si % SECTOR_COLORS.length] }} />
                                      {label}
                                    </Box>
                                  </TableCell>
                                  {summaries.map((s, ai) => {
                                    const sec = s.sectors.find((x) => x.sector === si + 1);
                                    const volML = sec ? sec.totalVolume / 1000 : 0;
                                    const cfu = volML > 0 ? Math.round(sec.totalCount / volML) : null;
                                    return (
                                      <TableCell key={ai} align="right">
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                          {cfu != null ? cfu.toLocaleString() : '-'}
                                        </Typography>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </>
                  );
                })() : (
                  <Paper sx={{ bgcolor: '#1e1e2e', p: 4, borderRadius: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Spiral plate analysis not available for the selected analyses.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

          </>
        )}

        {hasCompared && !hasAnyData && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No comparison data could be loaded. Ensure the selected analyses contain saved results.
          </Alert>
        )}

        {!hasCompared && !comparing && selected.length >= 2 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            Press "Compare" to load and overlay analysis data.
          </Typography>
        )}
      </Box>

      <React.Suspense fallback={null}>
        <HSVVisualization
          isOpen={hsvExplorerOpen}
          onClose={() => { setHsvExplorerOpen(false); setHsvExplorerData(null); setHsvExplorerLabel(''); }}
          objectMeta={typeof hsvExplorerLabel === 'object' ? hsvExplorerLabel : {}}
          preloadedData={hsvExplorerData}
        />
      </React.Suspense>
    </Box>
  );
}
